import { useEffect, useState } from "react";
import usePageTitle from "../../core/hooks/UsePageTitle";
import { Usuario } from "../../sdk/@types";
import UsuarioService from "../../sdk/services/UsuarioService";
import DefaultLayout from "../layouts/default/Default.layout";
import { Button, Col, Divider, Form, Input, notification, Row, Select, Switch, Tag } from "antd";
import { useLocation } from "react-router-dom";
import AuthService from "../auth/Authorization.service";
import AuthorizationService from "../../sdk/services/AuthService";

const profiles = [
  { id: "administrador", name: "Administrador" },
  { id: "basico", name: "Básico" },
  { id: "master", name: "Master" },
  { id: "competidor", name: "Competidor" },
  { id: "free", name: "Free" },
];

export default function UsuarioView() {
  const [form] = Form.useForm<Usuario.Usuario>();
  const location = useLocation();
  const [userReturned, setUserReturned] = useState<Usuario.Usuario | null>(location.state?.usuario ?? null);
  const [loggedProfile, setLoggedProfile] = useState<string>("");
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    const token = AuthService.getAccessToken();
    if (!token) return;
    AuthorizationService.getUser(token)
      .then((u) => setLoggedProfile(u.profile ?? ""))
      .catch(() => {});
  }, []);

  const canManageStatus = loggedProfile === "administrador" || loggedProfile === "master";

  const handleStatusChange = async (active: boolean, emailVerified: boolean) => {
    if (!userReturned?.id) return;
    setStatusLoading(true);
    try {
      await UsuarioService.setUserStatus(userReturned.id, active, emailVerified);
      setUserReturned((prev) => prev ? { ...prev, active, emailVerified } : prev);
      notification.success({ message: "Status atualizado com sucesso" });
    } catch {
      notification.error({ message: "Erro ao atualizar status" });
    } finally {
      setStatusLoading(false);
    }
  };

  const isPasswordDisabled = () => {
    if (userReturned && userReturned.id) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (userReturned) {
      form.setFieldsValue(userReturned);
    }
  }, [userReturned, form]);

  usePageTitle("Usuário");
  return (
    <DefaultLayout>
      <div>
        <Form
          form={form}
          onFinish={async (usuario: Usuario.Usuario) => {
            try {
              if (userReturned?.id) {
                const userToUpdate = {
                  ...userReturned,
                  ...usuario,
                };
                await UsuarioService.atualizarUsuario(userToUpdate).then((response:any) => {
                  setUserReturned(response);

                  if (response) {
                    form.setFieldsValue(response);
                  }                  

                  notification.info({
                    message: "Sucesso",
                    description: "Usuário atualizado com sucesso.",
                  });
                });
              }else{
                await UsuarioService.inserirNovoUsuario(usuario);

                notification.success({
                  message: "Sucesso",
                  description: "Usuário cadastrado com sucesso.",
                });
                form.resetFields();
              }
            } catch (error) {
              notification.error({
                message: "Houve um erro",
              });
            }
          }}
          layout={"vertical"}
          autoComplete={"off"}
        >
          <Row gutter={16}>

            <Col span={8}>
              <Form.Item label={"Usuário"} name={"userName"}>
                <Input placeholder={"Ex.: João"} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={"Nome Completo"} name={["fullName"]}>
                <Input placeholder={"Ex.: João Cruz"} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label={"Email"}
                name={"email"}
                rules={[
                  {
                    required: true,
                    message: "Campo obrigatório",
                  },
                ]}
              >
                <Input placeholder={"Ex.: contato@joao.batista"} maxLength={254} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item 
              label={"Perfil"} 
              name={["profile"]}
              rules={[
                {
                  required: true,
                  message: "Campo obrigatório",
                },
              ]}              
              >
                <Select
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option!.children as unknown as string).includes(input)
                  }
                >
                  {profiles.map((profile) => (
                    <Select.Option key={profile.id} value={profile.id}>
                      {profile.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>            
            <Col span={4}>
              <Form.Item
                label={"Password"}
                name={"password"}
                rules={[
                  {
                    required: !isPasswordDisabled(),
                    message: "Campo obrigatório",
                  },
                ]}
              >
                <Input.Password disabled={isPasswordDisabled()} placeholder={"Ex.: sua-senha"} />
              </Form.Item>
            </Col>            
            {canManageStatus && userReturned?.id && (
              <Col span={24}>
                <Divider orientation="left">Ativação da conta</Divider>
                <Row gutter={32} align="middle">
                  <Col>
                    <span className="mr-3 font-medium">Conta ativa:</span>
                    <Switch
                      checked={!!userReturned.active}
                      loading={statusLoading}
                      onChange={(checked) =>
                        handleStatusChange(checked, !!userReturned.emailVerified)
                      }
                    />
                    <Tag
                      className="ml-3"
                      color={userReturned.active ? "green" : "red"}
                    >
                      {userReturned.active ? "Ativa" : "Inativa"}
                    </Tag>
                  </Col>
                  <Col>
                    <span className="mr-3 font-medium">E-mail verificado:</span>
                    <Switch
                      checked={!!userReturned.emailVerified}
                      loading={statusLoading}
                      onChange={(checked) =>
                        handleStatusChange(!!userReturned.active, checked)
                      }
                    />
                    <Tag
                      className="ml-3"
                      color={userReturned.emailVerified ? "green" : "orange"}
                    >
                      {userReturned.emailVerified ? "Verificado" : "Pendente"}
                    </Tag>
                  </Col>
                </Row>
              </Col>
            )}

            <Col span={24}>
              <Button type={"primary"} htmlType={"submit"}>
                Enviar dados
              </Button>
            </Col>

          </Row>  
        </Form>
      </div>
    </DefaultLayout>
  );
}
